import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { checkSuspension } from '../services/suspensionCheck';
import { setupKeysAndDevice } from '../services/signingSetup';
import type { HydrationDeps } from '../types';

function withTimeout<T>(label: string, p: Promise<T>, ms: number, fallback: T | null = null): Promise<T | null> {
  return new Promise((resolve) => {
    let done = false;
    const t = setTimeout(() => {
      if (done) return;
      done = true;
      console.warn(`[useAuthHydration] ${label} timed out after ${ms}ms`);
      resolve(fallback);
    }, ms);
    p.then((v) => {
      if (done) return;
      done = true;
      clearTimeout(t);
      resolve(v);
    }).catch((e) => {
      if (done) return;
      done = true;
      clearTimeout(t);
      console.warn(`[useAuthHydration] ${label} failed`, e);
      resolve(fallback);
    });
  });
}

export function useAuthHydration(deps: HydrationDeps) {
  const hydrateUser = useCallback(async (userId: string, checkSusp = false) => {
    console.log('[useAuthHydration] start', { userId, checkSusp });
    try {
      // 1. Suspension check (early exit)
      if (checkSusp) {
        const suspended = await withTimeout('checkSuspension', checkSuspension(userId), 4000, false);
        if (suspended) {
          toast('Account Suspended', {
            description: 'Your account has been suspended. Please contact support for assistance.',
          });
          await supabase.auth.signOut();
          return;
        }
      }

      // 2. CORE hydration (profile, credits, admin) — bounded; we mark
      //    auth ready as soon as these settle (or time out).
      const coreResults = await Promise.allSettled([
        withTimeout('fetchProfile', Promise.resolve(deps.fetchProfile(userId)), 6000, null),
        withTimeout('fetchCredits', Promise.resolve(deps.fetchCredits(userId)), 6000, null),
        withTimeout('fetchAdminStatus', Promise.resolve(deps.fetchAdminStatus(userId)), 6000, null),
      ]);
      console.log('[useAuthHydration] core complete', coreResults.map(r => r.status));

      // Self-heal profile once if it came back null
      const profileResult = coreResults[0];
      if (profileResult.status === 'fulfilled' && profileResult.value === null) {
        await new Promise(r => setTimeout(r, 1500));
        await withTimeout('fetchProfile.retry', Promise.resolve(deps.fetchProfile(userId)), 4000, null);
      }

      // 3. BACKGROUND extras (crypto/device/trust). Never block auth-ready.
      void (async () => {
        try {
          const key = await withTimeout('setupKeysAndDevice', setupKeysAndDevice(userId), 8000, null);
          try { deps.setPrivateKey((key ?? null) as unknown as CryptoKey); } catch { /* ignore */ }
        } catch (e) {
          logger.warn('setupKeysAndDevice background failed', e);
        }
        try {
          await withTimeout('evaluateDeviceTrust', Promise.resolve(deps.evaluateDeviceTrust(userId)), 6000, null);
        } catch (e) {
          logger.warn('evaluateDeviceTrust background failed', e);
        }
        console.log('[useAuthHydration] background extras complete');
      })();
    } catch (e) {
      logger.error('Auth hydration failed:', e);
    } finally {
      console.log('[useAuthHydration] setIsAuthReady(true)');
      deps.setIsAuthReady(true);
    }
  }, [deps]);

  return { hydrateUser };
}
